import React, { useState, useEffect } from 'react';
import LoginForm from './LoginForm';
import DriverDashboard from './DriverDashboard';
import AdminDashboard from './AdminDashboard';
import SuperadminDashboard from './SuperadminDashboard';
import ResetPassword from './ResetPassword';
import PlatformAnnouncementBanner from './PlatformAnnouncementBanner';
import PaymentRequiredScreen from './PaymentRequiredScreen';
import { useAppContext } from '@/contexts/AppContext';
import { useTenantPaymentStatus } from '@/hooks/useTenantPaymentStatus';

// A Supabase Auth recovery/invite link lands here as
// `#access_token=...&refresh_token=...&type=recovery` (or `type=invite`)
// on whatever the project's Auth Site URL redirects to -- checked once on
// load, before any other routing, so it works regardless of exactly which
// path the redirect happens to land on. See ResetPassword.tsx.
const hasPasswordSetupLink = () =>
  /type=(recovery|invite)/.test(window.location.hash);

interface User {
  name: string;
  role: 'driver' | 'admin';
  vehicleId?: string;
}

const AppLayout: React.FC = () => {
  const { currentUser, logout, effectivePermissionsResolved, isSuperadmin } = useAppContext();
  const { paymentRestricted } = useTenantPaymentStatus();
  const [user, setUser] = useState<User | null>(null);
  // Lazy-initialised so the hash is only ever checked once, on the very
  // first render -- ResetPassword itself clears the hash from the URL
  // once it's done with it, so this must not re-evaluate afterward.
  const [showPasswordSetup, setShowPasswordSetup] = useState(hasPasswordSetupLink);

  // Load stored user role selection on first render
  useEffect(() => {
    const stored = localStorage.getItem('loggedUser');
    if (stored) {
      setUser(JSON.parse(stored));
    }
  }, []);

  const handleLogin = (userData: { name: string; role: 'driver' | 'admin'; vehicleId?: string }) => {
    setUser(userData);
    localStorage.setItem('loggedUser', JSON.stringify(userData));
  };

  const handleLogout = () => {
    setUser(null);
    logout();
    localStorage.removeItem('loggedUser');
  };

  // Checked before everything else, including isSuperadmin -- a recovery
  // link establishes its own temporary session via setSession() inside
  // ResetPassword, which AppContext's own session-restore effect would
  // otherwise race against / get confused by if the normal dashboards
  // rendered first.
  if (showPasswordSetup) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-800">
        <ResetPassword onDone={() => setShowPasswordSetup(false)} />
      </div>
    );
  }

  // Checked before the normal currentUser/Driver routing below -- a
  // superadmin isn't a member of any tenant, so there's no Driver record
  // and no `user` role to route on. See AppContext.tsx's isSuperadmin.
  if (isSuperadmin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-800">
        <SuperadminDashboard onLogout={handleLogout} />
      </div>
    );
  }

  if (!currentUser || !user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-800">
        <LoginForm onLogin={handleLogin} />
      </div>
    );
  }

  // Holds off rendering any dashboard content until effectivePermissions
  // has actually resolved for this session -- otherwise a restricted
  // user could briefly see a flash of full/unscoped data (every tab, or
  // every driver's data) before their real, restricted scope kicks in a
  // moment later. See AppContext.tsx's effectivePermissions effect.
  if (!effectivePermissionsResolved) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-black to-gray-800">
        <p className="text-muted-foreground text-sm">Loading...</p>
      </div>
    );
  }

  if (user.role === 'admin') {
    // Drivers aren't blocked by this -- billing is an admin concern, and
    // a tenant's drivers still need to work while it gets sorted out.
    // See PaymentRequiredScreen / stripe-webhook for how this flag flips.
    if (paymentRestricted) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-800">
          <PaymentRequiredScreen onLogout={handleLogout} />
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-800">
        <PlatformAnnouncementBanner />
        <AdminDashboard
          adminName={user.name}
          onLogout={handleLogout}
        />
      </div>
    );
  }

  if (user.role === 'driver') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-800">
        <PlatformAnnouncementBanner />
        <DriverDashboard
          driverName={user.name}
          vehicleId={user.vehicleId}
          onLogout={handleLogout}
          isImpersonating={false}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-800">
      <LoginForm onLogin={handleLogin} />
    </div>
  );
};

export default AppLayout;
